import React from 'react';
import { m as motion, useTransform } from 'framer-motion';

import { useSteppedAnimation } from '../hooks/useSteppedAnimation';

import logoData from '../assets/logo';
import * as css from './Logo.module.css';

/**
 * Abstracts a single brick (rectangle + character) of the
 * logo.
 */
const LogoBrick = (props) => {
  const { progress, brick } = props;
  const { char, frames } = brick;

  const groupTop = useTransform(progress, (latest) => {
    const currentBrickState = frames[latest];
    if (!currentBrickState) return null;

    return `calc(${currentBrickState.y} * var(--logo-ratio) * var(--logo-height))`;
  });

  const groupLeft = useTransform(progress, (latest) => {
    const currentBrickState = frames[latest];
    if (!currentBrickState) return null;

    return `calc(${currentBrickState.x} * var(--logo-width) + 0px)`;
  });

  const textTransform = useTransform(progress, (latest) => {
    const currentBrickState = frames[latest];
    if (!currentBrickState) return null;
    return `translate(calc(${currentBrickState.charX} * var(--logo-width)), 0)`;
  });

  const rectWidth = useTransform(progress, (latest) => {
    return `calc(${frames[latest].w} * var(--logo-width) + 1px)`;
  });

  return (
    <motion.div
      className={css.logoBrick}
      style={{
        x: groupLeft,
        y: groupTop,
        width: rectWidth,
        height: 'var(--logo-scaled-row-height)'
      }}>
      <motion.span
        className={css.logoLetter}
        style={{ transform: textTransform }}
        fill="currentColor">
        {char}
      </motion.span>
    </motion.div>
  );
};

/**
 * A simplified version of the logo for use in the header.
 * It starts animating on hover, speeds up and slows down
 */
const Logo = ({ logo = logoData.logo, style, ...rest }) => {
  const [progress, actions] = useSteppedAnimation({
    steps: logo.numFrames,
    slowDownDuration: 4
  });

  /**
   * This is done using HTML elements over SVG because Framer Motion
   * would automatically add transform-origin when animating SVG elements,
   * which isn't fully supported by Safari. This would cause the logo to
   * sometimes be cropped in half.
   *
   * While the cleanest solution would be to figure out a way to fix the
   * underlying framer issue, the easiest way to fix this for launch was
   * to simply use HTML elements. Given that we use the same font for all
   * our body texts too the appearance is consistent once the font has
   * been loaded.
   *
   * TODO: Investigate if upgrading to a later Framer version (which would
   * also require a Gatsby and React upgrade) could fix this issue.
   *
   * @see https://github.com/framer/motion/issues/255
   */
  return (
    <div
      role="img"
      aria-label="Design Systems International"
      className={css.root}
      style={{
        '--logo-ratio': logo.ratio,
        '--logo-row-height': logo.rowHeight,
        fontSize: 'var(--logo-font-size)',
        width: 'var(--logo-width)',
        height: 'var(--logo-height)',
        ...style
      }}
      {...rest}
      onPointerEnter={() => actions.start()}
      onPointerLeave={() => actions.stop()}
      onClick={() => actions.stop()}>
      {logo.chars.map((brick, idx) => {
        return <LogoBrick key={idx} brick={brick} progress={progress} />;
      })}
    </div>
  );
};

export default Logo;
